import ScreenMargin from '@/components/ScreenMargin';
import { ArrayElement, SortState } from '@/utils/typeHelpers';
import { ListImplantsQuery, useDeleteImplantMutation, useListImplantsLazyQuery } from '@/data/data';
import { Border, Color, FontFamily, FontSize, Padding } from '@/GlobalStyles';
import { IconButton, Text } from 'react-native-paper';
import { Redirect, router } from 'expo-router';
import DataTable, { getOrderByClause } from '@/components/DataTable/DataTable';
import { useCallback, useEffect, useState } from 'react';
import { useRefetchOnFocus } from '@/hooks/useRefetchOnFocus';
import { Alert, Pressable, StyleSheet, View } from 'react-native';
import Button from '@/components/Button';
import TextInput from '@/components/Form/TextInput';
import { Image } from 'expo-image';
import { useAuthContext } from '@/components/Auth/AuthContext';
import { getAdminLink, getNormalLink } from '@/components/Apollo/client';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

const ImplantIndexPage = () => {
  const client = useApolloClient();
  const [loadImplants, { loading, data, refetch }] = useListImplantsLazyQuery();
  const authUser = useAuthContext();
  const [sortState, setSortState] = useState<SortState<ArrayElement<ListImplantsQuery['implant']>>>(
    {
      key: 'name',
      sort: 'ascending',
    }
  );
  const [lastChange, setLastChange] = useState('');
  const [search, setSearch] = useState<string | undefined>();
  const { t } = useTranslation();

  useRefetchOnFocus(
    useCallback(
      () =>
        refetch({
          orderBy: getOrderByClause(sortState),
          where:
            search !== undefined && search !== ''
              ? {
                  name: {
                    _ilike: `%${search}%`,
                  },
                }
              : undefined,
        }),
      [sortState, lastChange]
    )
  );

  useEffect(() => {
    loadImplants({
      variables: {
        orderBy: getOrderByClause(sortState),
        where:
          search !== undefined && search !== ''
            ? {
                name: {
                  _ilike: `%${search}%`,
                },
              }
            : undefined,
      },
    });
  }, [sortState, lastChange]);

  const [deleteImplant] = useDeleteImplantMutation();

  if (authUser.payload && authUser.payload['cognito:groups'].indexOf('admin') <= -1) {
    return <Redirect href={'/patients'} />;
  }

  return (
    <ScreenMargin>
      <DataTable<ArrayElement<ListImplantsQuery['implant']>>
        forceListType={'list'}
        headerSideComponent={
          <Button
            onPress={() => router.push('/implants/ajouter')}
            style={{ alignSelf: 'flex-end' }}
          >
            Ajouter
          </Button>
        }
        subHeaderComponent={
          <View style={{ flexDirection: 'row', marginTop: 12, justifyContent: 'space-between' }}>
            <TextInput
              name={'search'}
              onChangeValue={(text) => {
                setSearch(text);
                if (text === '' || text === undefined) {
                  setLastChange(new Date().toISOString());
                }
              }}
              label={'Recherche'}
              containerStyle={{ marginTop: 0, flex: 1 }}
            />
            <Pressable
              style={styles.iconContainer}
              onPress={() => setLastChange(new Date().toISOString())}
            >
              <Image
                style={styles.icon}
                contentFit="cover"
                source={require('@assets/icones/loupe.png')}
              />
            </Pressable>
          </View>
        }
        dataTotalCount={10}
        title={t('Implants')}
        loading={loading}
        data={data?.implant}
        sortByState={sortState}
        onHeaderPress={(item) => {
          setSortState({
            key: item.key,
            sort:
              sortState.key === item.key && sortState.sort === 'ascending'
                ? 'descending'
                : 'ascending',
          });
        }}
        keyExtractor={(item) => `implant_${item.id}`}
        headers={[
          {
            title: t('NOM'),
            key: 'name',
            sortable: true,
            textStyle: {
              fontSize: FontSize.labelMedium_size,
              fontFamily: FontFamily.labelMedium,
              color: Color.MainNoir,
              fontWeight: '600',
              wordWrap: 'wrap',
              whiteSpace: 'wrap',
            },
            value: (item) => {
              return <Text style={{
                fontSize: FontSize.labelMedium_size,
                fontFamily: FontFamily.labelMedium,
                color: Color.MainNoir,
                fontWeight: '600',
                wordWrap: 'wrap',
                whiteSpace: 'wrap',
              }}>{item.name}</Text>;
            },
          },
          {
            title: t('ACTIONS'),
            style: { justifyContent: 'flex-end' },
            key: 'id',
            value: (item) => {
              return (
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    flex: 2,
                    minWidth: 120,
                    justifyContent: 'flex-end',
                    marginRight:- 10
                  }}
                >
                  <IconButton
                    icon="pencil"
                    onPress={() => router.push(`/implants/${item.id}/maj`)}
                    iconColor={Color.MainBleu}
                    style={{ margin: 0 }}
                  />
                  <IconButton
                    icon="delete"
                    onPress={() => {
                      Alert.alert('Attention', 'Etes-vous sûr de vouloir supprimer cet implant ?', [
                        {
                          text: 'Annuler',
                          style: 'cancel',
                        },
                        {
                          text: 'OK',
                          onPress: async () => {
                            client.setLink(getAdminLink());
                            await deleteImplant({
                              variables: { id: item.id },
                              fetchPolicy: 'network-only',
                            });
                            client.setLink(getNormalLink());
                            setLastChange(new Date().toISOString());
                          },
                        },
                      ]);
                    }}
                    iconColor={Color.error}
                    style={{ margin: 0 }}
                  />
                </View>
              );
            },
          },
        ]}
        onItemPress={(item) => {
          router.push(`/implants/${item.id}/maj`);
        }}
        emptyStateText={t("Aucun implant n'a été trouvé")}
        emptyStateLinkText={t('Ajouter un implant')}
        emptyStateLinkHref={'/implants/ajouter'}
      />
    </ScreenMargin>
  );
};

export default ImplantIndexPage;

const styles = StyleSheet.create({
  icon: {
    height: 22,
    width: 22,
    opacity: 0.3,
  },
  iconContainer: {
    padding: Padding.p_mini,
    height: 48,
    width: 48,
    backgroundColor: Color.BlancCasse,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: Border.br_11xl,
    flexDirection: 'row',
    marginLeft: 12,
  },
});
